import { Observable } from 'rxjs';
import { Permission, ReceiptResponse, ReceiptItemSubscriptionResponse, ReceiptItemParkingResponse, ReceiptItemPrepaidParkingResponse, ItemSubscriptionPayload, PaymentPayload } from '@bciti/types';
import {
    AdminRegisterPayload,
    CreateUserRecordPayload,
    FamilyLink,
    PatchApproveRefuseFamilyLink,
    PendingRequestCounts,
    Profile,
    ProfileResponse,
    UpdateUserRecordPayload,
    UserCardDiscrepanciesResponse,
    CardVerificationResponse,
    UserRecordResponse,
    UserResponse,
    GetUserActiveSubscriptionsResponse,
    LinkCardPayload,
    PostNewFamilyLink,
    RenewCardPayload,
    SubscribeCardPayload,
    UserCards,
    UserWallet,
    UserSearchQuery,
    PostUserFamilyLinkPayload,
    GetFamilyLinksItem,
    PostFamilyLinkPayload,
    UpdateFamilyLinkPayload,
    GetUserSubscriptionsResponse,
    GetUserRequests,
    UserRequest,
    BcitiPaginatedResult,
    UserActivityResponse,
    CardSearchResponse,
} from '../user.model';
import { PaginatedResult } from '../../app.model';

export abstract class UserService {
    abstract getProfile(): Observable<ProfileResponse>;

    abstract quickSearch(query: string, page: number, pageSize: number, filterBy?: string): Observable<PaginatedResult<UserResponse>>;

    abstract advancedSearch(queryObject: object, page?: number, pageSize?: number): Observable<PaginatedResult<UserResponse>>;

    abstract createUserRecord(payload: CreateUserRecordPayload): Observable<{ userId: string }>;

    abstract adminRegisterUser(payload: AdminRegisterPayload): Observable<{ userId: string }>;

    abstract updateUserRecord(profile: Partial<UpdateUserRecordPayload>): Observable<UserRecordResponse>;

    abstract updateUserRecordById(id: string, profile: Partial<UpdateUserRecordPayload>): Observable<UserRecordResponse>;

    abstract uploadProfilePicture(file: File): Observable<string>;

    abstract uploadProfilePictureById(id: string, file: File): Observable<string>;

    abstract getUser(userId: string): Observable<UserRecordResponse>;

    abstract adminManageRole(roleId: string, userId: string): Observable<Profile>;

    abstract removeUserRole(roleId: string, userId: string): Observable<any>;

    abstract deleteUser(id: string): Observable<boolean>;

    abstract getProfileById(id: string): Observable<ProfileResponse>;

    abstract sendConfirmAccountEmail(email: string): Observable<boolean>;

    abstract updateProfile(profile: Partial<UpdateUserRecordPayload>): Observable<ProfileResponse>;

    abstract approveUserCard(userId: string, cardId: string, subType: string, selectedExternalId?: string, selectedCardNumber?: string): Observable<boolean>;

    abstract approveNotConnectedUserCard(userId: string, cardId: string, subType: string, expDate: string, cardNumber: string): Observable<boolean>;

    abstract approveNotConnectedLinkUserCard(userId: string, cardId: string, subType: string, expDate: string): Observable<boolean>;

    abstract denyUserCard(userId: string, cardId: string, reason: string): Observable<boolean>;

    abstract deleteUserCard(userId: string, cardId: string): Observable<boolean>;

    abstract approveUserCardRenewal(userId: string, cardId: string, subType: string): Observable<boolean>;

    abstract approveNotConnectedUserCardRenewal(userId: string, cardId: string, subType: string, expDate: string, cardNumber: string): Observable<boolean>;

    abstract denyUserCardRenewal(userId: string, cardId: string, reason: string): Observable<boolean>;

    abstract patchApproveRefuseFamilyLink(body: PatchApproveRefuseFamilyLink): Observable<boolean>;

    abstract getUserFamily(userId: string): Observable<FamilyLink[]>;

    abstract cardAddressExists(cardId: string): Observable<Record<string, any>>;

    abstract getUserPermissions(): Observable<Permission[]>;

    abstract cardDiscrepancies(cardId: string, userId: string): Observable<UserCardDiscrepanciesResponse>;

    abstract cardVerification(cardId: string, userId: string): Observable<CardVerificationResponse>;

    abstract sendForgotPasswordEmail(email: string): Observable<boolean>;

    abstract postSubscribeCard(payload: SubscribeCardPayload, userId: string): Observable<UserCards>;

    abstract patchRenewalCard(payload: RenewCardPayload): Observable<UserCards>;

    abstract patchUpdateCard(payload: any, cardId: string);

    abstract getWallet(userWallet: string): Observable<UserWallet>;

    abstract postLinkCard(payload: LinkCardPayload): Observable<any>; // TODO: Review return type once api is ready

    abstract postLinkCardAdmin(payload: LinkCardPayload, userId: string): Observable<any>;

    abstract familyPostLinkCard(payload: LinkCardPayload, id: string): Observable<any>; // todo review return type

    abstract familyPatchRenewalCard(payload: RenewCardPayload, userId: string): Observable<UserCards>;

    abstract getFamilyMemberWallet(userId: string): Observable<UserWallet>;

    abstract getFamilyMemberSubscriptions(userId: string): Observable<GetUserActiveSubscriptionsResponse[]>;

    abstract getUserActiveSubscriptions(userId: string): Observable<GetUserActiveSubscriptionsResponse[]>;

    abstract getSystExtLink(connectorType: string): Observable<string>;

    abstract getOrganisationFeatures(tag: string): Observable<any>;

    abstract getAllCoreConfig(): Observable<any>;

    abstract getUserFamilyLinks(userId: string): Observable<any[]>;

    abstract postNewFamilyLink(userId: string, familyLink: PostNewFamilyLink): Observable<any>;

    abstract postDocuments(document: any): Observable<string[]>;

    abstract searchUser(payload: UserSearchQuery): Observable<any>;

    abstract postUserFamilyLink(payload: PostUserFamilyLinkPayload, sourceId: string, targetId: string): Observable<any>;

    abstract postSubscribeCards(payload: SubscribeCardPayload): Observable<UserCards>;

    abstract openCameraProfileError(error: string): void;

    abstract getUserFamilyLinksItem(userId: string): Observable<GetFamilyLinksItem[]>;

    abstract addFamilyLink(sourceId: string, familyLink: PostFamilyLinkPayload): Observable<GetFamilyLinksItem>;

    abstract modifyFamilyLink(sourceId: string, familyLink: UpdateFamilyLinkPayload): Observable<GetFamilyLinksItem>;

    abstract removeFamilyLink(sourceId: string, targetId: string): Observable<any>;

    abstract getUserSubscriptions(userId: string): Observable<GetUserSubscriptionsResponse>;

    abstract postPayment(payload: PaymentPayload<ItemSubscriptionPayload>, userId: string): Observable<any>;

    abstract patchSubscription(userId: string, subscriptionId: string, payload: any): Observable<any>;

    abstract deleteSubscription(userId: string, subscriptionId: string): Observable<any>;

    abstract getUserReceipts(
        id: string,
        page: number,
        pageSize: number,
    ): Observable<BcitiPaginatedResult<ReceiptResponse<ReceiptItemSubscriptionResponse | ReceiptItemParkingResponse | ReceiptItemPrepaidParkingResponse>>>;

    abstract getRequests(page: number, limit: number, userId: string): Observable<GetUserRequests>;

    abstract getRequestDetail(id: string): Observable<UserRequest>;

    abstract searchAdmins(query: string, limit?: number): Observable<any>;

    abstract getUserActivity(userId: string): Observable<UserActivityResponse>;

    abstract getPendingRequestCounts(): Observable<PendingRequestCounts>;

    abstract deleteUserAddress(userId: string, addressId: string): Observable<void>;

    abstract setMainAddress(userId: string, addressId: string): Observable<any>;

    abstract registerFamilyZitadelUser(payload: { email: string; firstName: string; lastName: string }): Observable<{ userId: string }>;

    abstract updateZitadelEmail(email: string | null, newEmail: string | null, orgId: string, bcitiUserId?: string): Observable<void>;

    abstract searchBeforeCreate(userId: string, payload: any): Observable<CardSearchResponse>;

    abstract adminPreCreateVerification(userId: string, cardData: { firstName?: string; lastName?: string; birthDate?: string; sex?: string }): Observable<CardVerificationResponse>;
}
